import React, { useEffect, useMemo, useState } from "react";
import { Landmark, Download } from "lucide-react";
import { api, exportUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, fmtDate } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, StatCard, Badge } from "@/components/common";

const METHODS = { transfer: "Transfer", cash: "Tunai", edc: "EDC/Kartu", other: "Lainnya" };
const MODES = [["range", "Rentang Tanggal"], ["month", "Bulanan"], ["year", "Rentang Tahun"]];

function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function AccountingReport() {
  const { branch } = useAuth();
  const now = new Date();
  const [mode, setMode] = useState("month");
  const [from, setFrom] = useState(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [to, setTo] = useState(iso(now));
  const [year, setYear] = useState(now.getFullYear());
  const [fromYear, setFromYear] = useState(now.getFullYear() - 2);
  const [toYear, setToYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const years = useMemo(() => Array.from({ length: 8 }, (_, i) => now.getFullYear() - i), [now]);

  const params = useMemo(() => {
    const p = branch ? { branch_id: branch.id } : {};
    if (mode === "range") return { ...p, date_from: from, date_to: to, group: "day" };
    if (mode === "month") return { ...p, date_from: `${year}-01-01`, date_to: `${year}-12-31`, group: "month" };
    return { ...p, date_from: `${fromYear}-01-01`, date_to: `${toYear}-12-31`, group: "year" };
  }, [branch, mode, from, to, year, fromYear, toYear]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/reports/accounting-expenses", params)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [params]);

  const periodLabel = mode === "year" ? "Tahun" : mode === "month" ? "Bulan" : "Tanggal";

  return (
    <>
      <PageHeader title="Laporan Pengeluaran Akunting" subtitle={branch?.name || "Semua Unit"} icon={Landmark}
        actions={
          <a href={exportUrl("accounting_expenses", params)} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 h-10 rounded-xl bg-primary text-white text-sm font-medium hover:opacity-90" data-testid="acc-rep-export">
            <Download size={15} /> Excel
          </a>
        } />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {MODES.map(([v, l]) => (
          <button key={v} onClick={() => setMode(v)} data-testid={`acc-rep-mode-${v}`}
            className={`px-4 h-9 rounded-xl text-sm font-medium ${mode === v ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
        ))}
      </div>

      <Card className="p-4 mb-6 flex flex-wrap items-end gap-3">
        {mode === "range" && (
          <>
            <label className="block"><span className="text-xs font-medium text-slate-500 mb-1 block">Dari Tanggal</span>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="acc-rep-from" /></label>
            <label className="block"><span className="text-xs font-medium text-slate-500 mb-1 block">Sampai Tanggal</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="acc-rep-to" /></label>
          </>
        )}
        {mode === "month" && (
          <label className="block"><span className="text-xs font-medium text-slate-500 mb-1 block">Tahun</span>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="acc-rep-year">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select></label>
        )}
        {mode === "year" && (
          <>
            <label className="block"><span className="text-xs font-medium text-slate-500 mb-1 block">Dari Tahun</span>
              <select value={fromYear} onChange={(e) => setFromYear(Number(e.target.value))} className="h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="acc-rep-from-year">
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select></label>
            <label className="block"><span className="text-xs font-medium text-slate-500 mb-1 block">Sampai Tahun</span>
              <select value={toYear} onChange={(e) => setToYear(Number(e.target.value))} className="h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="acc-rep-to-year">
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select></label>
          </>
        )}
      </Card>

      {loading ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="Total Pengeluaran" value={rupiah(data?.total || 0)} sub={`${data?.count || 0} transaksi`} tone="danger" icon={Landmark} testid="acc-rep-total" />
            {(data?.by_category || []).slice(0, 3).map((c) => (
              <StatCard key={c.key} label={c.label} value={rupiah(c.amount)} sub={`${c.count} transaksi`} />
            ))}
          </div>

          {data?.periods?.length ? (
            <Card className="p-5 mb-6">
              <h3 className="font-semibold text-slate-800 mb-3">Rincian per {periodLabel}</h3>
              <table className="w-full text-sm" data-testid="acc-rep-periods">
                <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                  <th className="py-2 font-medium">{periodLabel}</th>
                  <th className="py-2 font-medium text-right">Transaksi</th>
                  <th className="py-2 font-medium text-right">Jumlah</th>
                </tr></thead>
                <tbody>
                  {data.periods.map((p) => (
                    <tr key={p.period} className="border-b border-slate-50">
                      <td className="py-2.5 font-medium text-slate-700">{p.label}</td>
                      <td className="py-2.5 text-right text-slate-500">{p.count}</td>
                      <td className="py-2.5 text-right font-semibold">{rupiah(p.amount)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-slate-200 font-semibold text-slate-800">
                    <td className="py-2.5">TOTAL</td>
                    <td className="py-2.5 text-right">{data.count}</td>
                    <td className="py-2.5 text-right text-rose-600">{rupiah(data.total)}</td>
                  </tr>
                </tbody>
              </table>
            </Card>
          ) : null}

          {!data?.items?.length ? (
            <EmptyState icon={Landmark} title="Belum ada pengeluaran akunting" message="Tidak ada data pada periode yang dipilih." />
          ) : (
            <Card className="overflow-hidden">
              <table className="w-full text-sm" data-testid="acc-rep-items">
                <thead><tr className="text-left text-slate-400 border-b border-slate-100 bg-slate-50/50">
                  <th className="py-3 px-4 font-medium">Tanggal</th><th className="py-3 px-4 font-medium">Kategori</th>
                  <th className="py-3 px-4 font-medium">Vendor / Catatan</th><th className="py-3 px-4 font-medium">Metode</th>
                  <th className="py-3 px-4 font-medium text-right">Jumlah</th>
                </tr></thead>
                <tbody>
                  {data.items.map((e) => (
                    <tr key={e.id} className="border-b border-slate-50">
                      <td className="py-3 px-4 text-slate-400 text-xs">{fmtDate(e.created_at)}</td>
                      <td className="py-3 px-4"><Badge className="bg-primary/10 text-primary">{e.category_label}</Badge></td>
                      <td className="py-3 px-4 text-slate-600">{e.vendor || e.note || "-"}</td>
                      <td className="py-3 px-4 text-slate-500">{METHODS[e.method] || e.method}</td>
                      <td className="py-3 px-4 text-right font-semibold text-rose-600">{rupiah(e.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}
    </>
  );
}
